import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import API from "../api/api";
import Swal from "sweetalert2";
import "./AdminDashboard.css";

const EditCategory = () => {
  const { schemeId, categoryName } = useParams();

  const [category, setCategory] = useState(null);        // edited category
  const [allCategories, setAllCategories] = useState([]); // full categories list
  const [loading, setLoading] = useState(true);

  // ✅ Fetch full scheme and extract category
  useEffect(() => {
    const fetchCategory = async () => {
      try {
        const res = await API.get(`/schemes/${schemeId}`);
        const scheme = res.data;
        const decodedName = decodeURIComponent(categoryName);

        const found = scheme.categories.find(
          (c) => c.name === decodedName
        );

        if (found) {
          setCategory(found);
          setAllCategories(scheme.categories); // ✅ store all categories
        } else {
          await Swal.fire({
            icon: "error",
            title: "Not Found",
            text: "Category not found in this scheme.",
          });
          window.history.back();
        }
      } catch (err) {
        console.error("Error fetching category:", err);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load category data.",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [schemeId, categoryName]);

  // ✅ Safe category name update
  const handleCategoryChange = (value) => {
    setCategory((prev) => ({
      ...prev,
      name: value,
    }));
  };

  // ✅ Safe subcategory update
  const handleSubChange = (index, field, value) => {
    setCategory((prev) => ({
      ...prev,
      subcategories: prev.subcategories.map((sub, i) =>
        i === index ? { ...sub, [field]: value } : sub
      ),
    }));
  };

  // ✅ Add new subcategory
  const addSub = async () => {
    setCategory((prev) => ({
      ...prev,
      subcategories: [
        ...prev.subcategories,
        { name: "", applyLink: "", youtubeLink: "" },
      ],
    }));

    await Swal.fire({
      toast: true,
      icon: "success",
      title: "New subcategory added!",
      position: "top-end",
      showConfirmButton: false,
      timer: 1200,
    });
  };

  // ✅ Delete subcategory
  const deleteSub = async (index) => {
    const confirmed = await Swal.fire({
      title: "Delete Subcategory?",
      text: `Are you sure you want to remove "${category.subcategories[index].name}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirmed.isConfirmed) return;

    setCategory((prev) => ({
      ...prev,
      subcategories: prev.subcategories.filter((_, i) => i !== index),
    }));

    await Swal.fire({
      icon: "success",
      title: "Deleted!",
      timer: 1200,
      showConfirmButton: false,
    });
  };

  // ✅ Save updated category WITHOUT deleting others
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const updatedCategories = allCategories.map((cat) =>
        cat.name === decodeURIComponent(categoryName)
          ? category // replace only this one
          : cat
      );

      await API.put(`/schemes/${schemeId}`, {
        categories: updatedCategories,
      });

      await Swal.fire({
        icon: "success",
        title: "Success!",
        text: "Category updated successfully.",
        timer: 1500,
        showConfirmButton: false,
      });

      window.history.back();
    } catch (err) {
      console.error("Error updating category:", err);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to update category.",
      });
    }
  };

  // UI states
  if (loading) {
    return <p className="text-center mt-4">Loading category...</p>;
  }

  if (!category) {
    return <p className="text-center mt-4 text-danger">Category not found.</p>;
  }

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-card shadow-lg">
        <h3 className="dashboard-title mb-3 text-primary">
          Edit Service Type - {category.name}
        </h3>

        <form onSubmit={handleSubmit}>
          {/* ✅ Category Name */}
          <input
            className="form-control input-box mb-3"
            placeholder="Category Name"
            value={category.name}
            onChange={(e) => handleCategoryChange(e.target.value)}
            required
          />

          {/* ✅ Subcategories */}
          {category.subcategories.map((sub, i) => (
            <div key={i} className="subcategory-box mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <h6 className="text-secondary mb-0">
                  {i + 1}. Service Action
                </h6>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => deleteSub(i)}
                >
                  Remove
                </button>
              </div>

              <input
                className="form-control input-box mb-2"
                placeholder="Subcategory Name"
                value={sub.name}
                onChange={(e) =>
                  handleSubChange(i, "name", e.target.value)
                }
                required
              />

              <input
                className="form-control input-box mb-2"
                placeholder="Apply Link"
                value={sub.applyLink}
                onChange={(e) =>
                  handleSubChange(i, "applyLink", e.target.value)
                }
              />

              <input
                className="form-control input-box mb-2"
                placeholder="YouTube Link"
                value={sub.youtubeLink}
                onChange={(e) =>
                  handleSubChange(i, "youtubeLink", e.target.value)
                }
              />
            </div>
          ))}

          {/* ✅ Add Subcategory */}
          <button
            type="button"
            className="btn btn-sm btn-outline-success mb-3"
            onClick={addSub}
          >
            + Add Service Action
          </button>

          <br />

          <button type="submit" className="btn save-btn me-2">
            Save Changes
          </button>

          <button
            type="button"
            className="btn cancel-btn"
            onClick={() => window.history.back()}
          >
            Cancel
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditCategory;
